<template lang="pug">
yz-dialog(v-model="showDialog" fullscreen persistent)
  yz-card.d-flex.flex-column(style="height: 100%;")
    //- Header with conditional action buttons
    yz-dialog-fullscreen-header(:title="headerTitle" :loading="isSubmitting || isLoadingData" :disable-back="isSubmitting || isSavingDraft" @back="onBack")
      template(#actions)
        .d-flex.align-center(style="gap: 12px")
          //- Create mode or Draft status
          template(v-if="!isViewMode || einvoiceStatus === 'draft'")
            yz-btn(size="lg" text label="Cancel" :disabled="isSubmitting || isSavingDraft" @click="onClose")
            yz-btn(size="lg" secondary label="Save as draft" :loading="isSavingDraft" :disabled="isSubmitting" @click="onSaveDraft")
            yz-btn(size="lg" label="Submit e-Invoice" :loading="isSubmitting" :disabled="isSavingDraft" @click="onSubmit")
          //- Submitted status
          template(v-else-if="einvoiceStatus === 'submitted'")
            yz-btn(size="lg" label="Close" @click="onClose")
          //- Invalid status
          template(v-else-if="einvoiceStatus === 'invalid'")
            yz-btn(size="lg" text label="Cancel" @click="onClose")
            yz-btn(size="lg" label="Resubmit e-Invoice" :loading="isSubmitting" @click="onSubmit")
          //- Valid status
          template(v-else-if="einvoiceStatus === 'valid'")
            yz-btn(v-if="activePaymentMethod === 'panel'" size="lg" secondary label="Download PDF" :loading="isDownloading" @click="onDownloadPdf" append-icon="$yzi-download-02")
            yz-btn(size="lg" label="Close" @click="onClose")

    //- Content: Two-column layout
    yz-dialog-fullscreen-content.flex-grow-1.overflow-y-auto
      v-row(no-gutters)
        //- Left: Selected invoices table
        v-col(cols="12" md="9")
          .pr-md-6
            //- Section header
            .yz-heading-9.neutral700--text Selected invoices
            .yz-regular-body-xs.neutral700--text.mb-4 Review the invoices included in this consolidated e-Invoice submission before proceeding.

            //- Data table
            yz-card.pa-5
              yz-data-table(:headers="headers" :items="localInvoices" item-key="pid" :loading="isLoadingData" :items-per-page="pageSize" hide-default-footer)
                template(v-slot:no-data)
                  .py-8
                    .text-sm.neutral500--text.text-center No invoices available

                //- Date column
                template(v-slot:item.date="{ item }")
                  div.yz-regular-body-sm.neutral700--text {{ item.date | humanizeDate }}

                //- Payment amount column
                template(v-slot:item.amount="{ item }")
                  div.yz-medium-body-sm.neutral700--text {{ item.amount | displayPrice($currency) }}

                //- Patient name column
                template(v-slot:item.patient="{ item }")
                  div.yz-regular-body-sm.neutral700--text {{ item.patient }}

                //- Invoice number column
                template(v-slot:item.invoice_no="{ item }")
                  div.yz-regular-body-sm.neutral700--text Bill {{ item.invoice_no }}

                //- Payment method column
                template(v-slot:item.payment_methods="{ item, index }")
                  div(:id="`consolidate-pm-${index}`" style="position: relative;")
                  div.d-flex.align-center.yz-regular-body-sm
                    template(v-for="(method, idx) in getDisplayedPaymentMethods(item.payment_methods)")
                      y-chip(:key="idx" :label="method" size="sm" chip-class="pa-1 mr-2" outlined)
                    v-tooltip(v-if="item.payment_methods && item.payment_methods.length > 2" top :attach="`#consolidate-pm-${index}`" color="neutral700")
                      template(#activator="{ on, attrs }")
                        div(v-bind="attrs" v-on="on")
                          y-chip.cursor-help(:label="`+${item.payment_methods.length - 2} more`" size="sm" chip-class="pa-1" outlined)
                      div
                        div(v-for="(method, idx) in item.payment_methods" :key="idx") {{ idx + 1 }}. {{ method }}

                //- Actions column
                template(v-slot:item.actions="{ item }")
                  yz-btn(outlined size="sm" icon="$yzi-arrow-narrow-up-right" @click.stop="onOpenInvoice(item)")

              //- Footer: Total + Pagination
              .d-flex.align-center.mt-4
                .text-sm.neutral600--text Total:
                  span.font-semibold  {{ totalAmount | displayPrice($currency) }}
                  |  ({{ paginationCount }} {{ paginationCount === 1 ? 'invoice' : 'invoices' }})
                v-spacer
                yz-pagination(v-if="paginationCount > pageSize" v-model="currentPage" :page-size="pageSize" :count="paginationCount" type="page" arrow @input="onInvoicePageChange")

        v-divider.yz-neutral-300(vertical)
        //- Right: e-Invoice details sidebar
        v-col(cols="12" md="3")
          v-form.pa-5(ref="einvoiceForm")
            .yz-heading-9.neutral700--text e-Invoice details
            .yz-regular-body-xs.neutral700--text.mb-4 Key information related to the submitted e-Invoice.

            //- Validation error box
            .mb-4.pa-4.rounded-lg(v-if="einvoiceStatus === 'invalid' && validationErrors.length" style="background-color: #FFF7D9;")
              .yz-medium-body-xs.neutral700--text Validation error
              .yz-regular-body-xs.danger500--text.mt-1(v-for="(error, index) in validationErrors" :key="index")
                v-icon.mr-1(v-if="validationErrors.length > 1" size="10" color="danger500") mdi-record
                span {{ error }}

            //- Total e-Invoice with optional status chip
            .mb-4.pa-4.rounded-lg.bg-neutral-300
              v-row(no-gutters align="center")
                v-col
                  .yz-regular-body-xs.neutral600--text Total e-Invoice
                  .yz-heading-5.neutral700--text.mt-1 {{ totalAmount | displayPrice($currency) }}
                v-col.shrink
                  y-chip(v-if="isViewMode && statusText" :label="statusText" :color="statusColor")
                    template(#prepend)
                      v-icon.mr-1(size="12" :color="statusIconColor") mdi-record
                  yz-btn.mt-2(v-if="einvoiceStatus === 'valid' && shareUrl" outlined size="sm" label="View e-Invoice" @click="onViewEInvoice" style="background-color: var(--v-neutral100);")

            //- Date section: editable or read-only
            template(v-if="isEditable")
              div.mb-4
                .yz-medium-body-sm.neutral700--text.mb-1 Submitted date
                yz-menu(offset-y min-width="auto")
                  template(v-slot:activator="{ on, attrs }")
                    yz-text-field(v-model="formattedSubmittedDate" placeholder="Pick a date" readonly clickable v-bind="attrs" v-on="on")
                      template(#append)
                        v-icon(color="neutral500" size="18") $yzi-calendar
                  yz-date-picker(v-model="submittedDate" scrollable)
            template(v-else)
              div.mb-4
                .yz-medium-body-sm.neutral700--text Validation date
                .yz-regular-body-sm.neutral700--text {{ formattedValidationDate }}

            //- Fields below date (hidden for single type)
            template(v-if="!isSingleType")
              //- Self-pay read-only
              template(v-if="activePaymentMethod === 'self-pay' && isReadOnly")
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Buyer name
                  .yz-regular-body-sm.neutral700--text General public
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Description
                  .yz-regular-body-sm.neutral700--text {{ description || '-' }}

              //- Self-pay editable
              template(v-else-if="activePaymentMethod === 'self-pay'")
                div.mb-4
                  .yz-medium-body-sm.neutral700--text.mb-1 Buyer name
                  .yz-regular-body-sm.neutral700--text General public
                div
                  .yz-medium-body-sm.neutral700--text.mb-1 Description
                  yz-text-field(v-model="description" placeholder="Enter e-Invoice description")

              //- Panel read-only
              template(v-else-if="isReadOnly")
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Company name
                  .yz-regular-body-sm.neutral700--text {{ companyName || '-' }}
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Tax Identification Number (TIN)
                  .yz-regular-body-sm.neutral700--text {{ tinNumber || '-' }}
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Phone number
                  .yz-regular-body-sm.neutral700--text {{ phoneNumber || '-' }}
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Email
                  .yz-regular-body-sm.neutral700--text {{ email || '-' }}
                div.mb-4
                  .yz-medium-body-sm.neutral700--text Address
                  .yz-regular-body-sm.neutral700--text(style="white-space: pre-line") {{ formattedAddress }}

              //- Panel editable
              template(v-else)
                div.mb-4
                  .yz-medium-body-sm.neutral700--text.mb-1 Company name
                  yz-text-field(v-model="companyName" placeholder="Enter company name")
                div.mb-4
                  .yz-medium-body-sm.neutral700--text.mb-1 TIN
                  yz-text-field(v-model="tinNumber" placeholder="Enter TIN number" required)
                div.mb-4
                  .yz-medium-body-sm.neutral700--text.mb-1 Phone
                  yz-text-field(v-model="phoneNumber" placeholder="Enter phone number")
                div.mb-4
                  .yz-medium-body-sm.neutral700--text.mb-1 Email
                  yz-text-field(v-model="email" placeholder="Enter email address")
                div.mb-4
                  yz-checkbox(v-model="sendCopyToPanel" label="Send copy to panel")
                .yz-medium-body-sm.neutral700--text.mb-2 Address
                yz-address-input(:line1.sync="address.line1" :line2.sync="address.line2" :postcode.sync="address.postcode" :city.sync="address.city" :state.sync="address.state" :country.sync="address.country" type="malaysia")

  //- Note prompt modal
  yz-dialog(v-model="showNotePrompt" persistent max-width="500")
    yz-card
      yz-dialog-normal-header(title="Payment amount change detected" @close="onCloseNotePrompt")
      yz-dialog-normal-content.px-6.py-6
        .d-flex.align-center.mb-2(style="gap: 8px")
          v-icon(size="20" color="danger500") $yzi-alert-circle
          .yz-medium-body-md.neutral700--text {{ notePromptTitle }}
        .yz-regular-body-sm.neutral700--text {{ notePromptDescription }}
      yz-dialog-normal-actions
        .d-flex.align-center.justify-end(style="gap: 12px")
          yz-btn(size="lg" text label="Close" @click="onCloseNotePrompt")
          yz-btn(size="lg" :label="notePromptButtonLabel" @click="onCreateNote")
          
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ConsolidateEInvoiceDialog',
  props: {
    paymentMethod: {
      type: String,
      default: 'self-pay',
      validator: (value) => ['self-pay', 'panel'].includes(value)
    },
    dateFilter: {
      type: [Object, Array],
      default: () => null
    },
    panelData: {
      type: Object,
      default: null
    },
    einvoiceId: {
      type: [Number, String],
      default: null
    }
  },
  data: () => ({
    isSubmitting: false,
    isSavingDraft: false,
    isLoadingData: false,
    isDownloading: false,
    showNotePrompt: false,
    notePromptType: null,
    description: '',
    currentPage: 1,
    pageSize: 20,
    totalInvoiceCount: 0,
    totalInvoiceAmount: 0,
    serverTotalAmount: null,
    localInvoices: [],
    showDialog: true,
    submittedDate: new Date().toISOString().split('T')[0],
    // View mode fields
    localPaymentMethod: 'self-pay',
    einvoiceStatus: null,
    statusText: '',
    validatedDate: null,
    shareUrl: null,
    localPanelName: '',
    localEinvoiceType: 'consolidated',
    validationErrors: [],
    // Panel-specific fields
    companyName: '',
    tinNumber: '',
    phoneNumber: '',
    email: '',
    sendCopyToPanel: false,
    address: {
      line1: '',
      line2: '',
      postcode: '',
      city: '',
      state: '',
      country: 'Malaysia'
    }
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    isViewMode() {
      return !!this.einvoiceId
    },
    isEditable() {
      return !this.isViewMode || ['draft', 'invalid'].includes(this.einvoiceStatus)
    },
    isReadOnly() {
      return this.isViewMode && !this.isEditable
    },
    headerTitle() {
      return this.isViewMode ? 'e-Invoice details' : 'Consolidate e-Invoice'
    },
    activePaymentMethod() {
      return this.isViewMode ? this.localPaymentMethod : this.paymentMethod
    },
    isSingleType() {
      return this.localEinvoiceType === 'single'
    },
    statusColor() {
      switch (this.einvoiceStatus) {
        case 'draft': return 'normal'
        case 'submitted': return 'blue'
        case 'valid': return 'green'
        case 'invalid': return 'red'
        default: return 'normal'
      }
    },
    statusIconColor() {
      switch (this.einvoiceStatus) {
        case 'draft': return 'neutral500'
        case 'submitted': return 'info500'
        case 'valid': return 'success500'
        case 'invalid': return 'danger500'
        default: return 'neutral500'
      }
    },
    formattedValidationDate() {
      if (!this.validatedDate) return '-'
      return this.$options.filters.humanizeDate(this.validatedDate)
    },
    formattedAddress() {
      const parts = []
      if (this.address.line1) parts.push(this.address.line1)
      if (this.address.line2) parts.push(this.address.line2)
      const cityLine = [this.address.postcode, this.address.city].filter(Boolean).join(' ')
      if (cityLine) parts.push(cityLine)
      if (this.address.state) parts.push(this.address.state)
      return parts.join(',\n') || '-'
    },
    headers() {
      return [
        { text: 'DATE', value: 'date', class: 'text-table-header' },
        { text: 'INVOICE NO.', value: 'invoice_no', class: 'text-table-header' },
        { text: 'PATIENT NAME', value: 'patient', class: 'text-table-header' },
        { text: 'PAYMENT METHOD', value: 'payment_methods', class: 'text-table-header', sortable: false },
        { text: 'PAYMENT AMOUNT', value: 'amount', class: 'text-table-header' },
        { text: '', value: 'actions', class: 'text-table-header', sortable: false },
      ]
    },
    paginationCount() {
      return this.isViewMode ? this.totalInvoiceCount : this.localInvoices.length
    },
    totalAmount() {
      if (this.isViewMode && this.serverTotalAmount !== null) return this.serverTotalAmount
      return this.totalInvoiceAmount
    },
    formattedSubmittedDate() {
      if (!this.submittedDate) return ''
      return this.$options.filters.humanizeDate(this.submittedDate)
    },
    generatedDescription() {
      if (!this.dateFilter) return ''

      // Single date: { key: 'date', value: '2025-12-10' }
      if (this.dateFilter.key === 'date') {
        const formatted = this.$options.filters.humanizeDate(this.dateFilter.value)
        return `Sales of ${formatted}`
      }

      // Range: [{ key: 'date__gte', value: '...' }, { key: 'date__lte', value: '...' }]
      if (Array.isArray(this.dateFilter)) {
        const startFilter = this.dateFilter.find(f => f.key === 'date__gte')
        const endFilter = this.dateFilter.find(f => f.key === 'date__lte')

        if (startFilter && endFilter) {
          const startDate = new Date(startFilter.value)
          const endDate = new Date(endFilter.value)

          // Check if full month (1st to last day)
          const isFirstDay = startDate.getDate() === 1
          const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate()
          const isLastDay = endDate.getDate() === lastDayOfMonth
          const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()

          if (isFirstDay && isLastDay && sameMonth) {
            // Full month: "Sales of December 2025"
            const monthName = startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            return `Sales of ${monthName}`
          }

          // Partial range: "Sales of 1 Dec 2025 to 15 Dec 2025"
          const startFormatted = this.$options.filters.humanizeDate(startFilter.value)
          const endFormatted = this.$options.filters.humanizeDate(endFilter.value)
          return `Sales of ${startFormatted} to ${endFormatted}`
        }
      }

      return ''
    },
    notePromptTitle() {
      return this.notePromptType === 'credit'
        ? 'Do you want to submit a Credit Note to LHDN?'
        : 'Do you want to submit a Debit Note to LHDN?'
    },
    notePromptDescription() {
      return this.notePromptType === 'credit'
        ? 'The updated amount is lower than the validated e-Invoice. A Credit Note issuance is required to reflect these changes.'
        : 'The updated amount is higher than the validated e-Invoice. A Debit Note issuance is required to reflect these changes.'
    },
    notePromptButtonLabel() {
      return this.notePromptType === 'credit' ? 'Create credit note' : 'Create debit note'
    }
  },
  mounted() {
    this.initializeData()
  },
  methods: {
    ...mapActions(['showMessage']),
    initializeData() {
      this.currentPage = 1

      if (this.einvoiceId) {
        // View mode: fetch from API
        this.fetchEinvoiceData()
        return
      }

      // Create mode: fetch from API using filter params
      this.submittedDate = new Date().toISOString().split('T')[0]
      this.$nextTick(() => {
        this.description = this.generatedDescription || ''
      })
      // Pre-fill panel fields from panelData if available
      if (this.panelData && this.paymentMethod === 'panel') {
        const panel = this.panelData.panel
        if (panel) {
          this.companyName = panel.company_name || ''
          this.tinNumber = panel.tin || ''
          this.phoneNumber = panel.phone || ''
          this.email = panel.email || ''
          this.mapAddress(panel.address)
        }
      }
      this.fetchCreateModeInvoices()
    },

    fetchCreateModeInvoices() {
      this.isLoadingData = true
      const params = {
        company: this.company.id,
        page: this.currentPage,
        page_size: this.pageSize,
        ...this.getDateRange()
      }
      // Add panel ID if panel payment
      if (this.paymentMethod === 'panel' && this.panelData) {
        params.panel = this.panelData.panel?.id || ''
      }
      this.$axios.get('/einvoices/eligible_invoices/', { params })
        .then(resp => {
          this.totalInvoiceCount = resp.data?.count || 0
          this.totalInvoiceAmount = resp.data?.total_amount || 0
          this.mapInvoiceList(resp.data?.results || [])
        })
        .catch(err => { this.handleApiError(err) })
        .finally(() => { this.isLoadingData = false })
    },

    fetchEinvoiceData() {
      this.isLoadingData = true
      const id = this.einvoiceId

      Promise.all([
        this.$axios.get(`/einvoices/${id}/`),
        this.$axios.get(`/einvoices/${id}/invoices`, { params: { page: this.currentPage, page_size: this.pageSize } })
      ])
        .then(([detailResp, invoicesResp]) => {
          this.mapEinvoiceDetail(detailResp.data)
          this.totalInvoiceCount = invoicesResp.data?.count || 0
          this.mapInvoiceList(invoicesResp.data?.results || [])
        })
        .catch(err => {
          this.handleApiError(err)
          this.$emit('close')
        })
        .finally(() => {
          this.isLoadingData = false
        })
    },

    mapEinvoiceDetail(data) {
      this.serverTotalAmount = data.total_amount || 0
      this.einvoiceStatus = data.status
      this.statusText = data.status_text || ''
      this.localEinvoiceType = data.type || 'consolidated'
      this.localPaymentMethod = data.panel ? 'panel' : 'self-pay'
      this.localPanelName = data.panel || ''
      this.submittedDate = data.submitted_date ? data.submitted_date.split('T')[0] : null
      this.validatedDate = data.validated_date
      this.shareUrl = data.share_url || null
      this.description = data.date_description || ''
      this.sendCopyToPanel = data.send_email || false

      // Panel fields from customer_* fields
      this.companyName = data.customer_name || ''
      this.tinNumber = data.customer_tin || ''
      this.phoneNumber = data.customer_phone || ''
      this.email = data.customer_email || ''

      this.mapAddress(data.customer_address)

      // Extract validation errors from LHDN result
      this.validationErrors = []
      const steps = data.result?.validationResults?.validationSteps || []
      steps.forEach(step => {
        if (step.status === 'Invalid' && step.error?.innerError?.length && step.error.innerError[0].error) {
          this.validationErrors.push(step.error.innerError[0].error)
        }
      })

      // Check if note creation is required
      if (data.debit_note_required) {
        this.notePromptType = 'debit'
        this.showNotePrompt = true
      } else if (data.credit_note_required) {
        this.notePromptType = 'credit'
        this.showNotePrompt = true
      }
    },

    mapInvoiceList(results) {
      const channel = this.activePaymentMethod === 'panel' ? 'panel' : 'self-pay'
      const panelName = this.isViewMode ? this.localPanelName : (this.panelData?.name || '')
      this.localInvoices = results.map(item => ({
        pid: item.pid,
        invoice_no: item.rid,
        patient: item.customer?.name || '-',
        date: item.record_date,
        payment_methods: (item.payment_methods || []).filter(pm => pm.channel === channel && (!panelName || pm.name === panelName)).map(pm => pm.name),
        amount: (item.transactions || []).filter(t => t.channel === channel && (!panelName || t.payment_method === panelName)).reduce((sum, t) => sum + (t.amount || 0), 0)
      }))
    },

    onInvoicePageChange() {
      if (!this.isViewMode) {
        this.fetchCreateModeInvoices()
        return
      }
      this.isLoadingData = true
      const id = this.einvoiceId
      this.$axios.get(`/einvoices/${id}/invoices`, { params: { page: this.currentPage, page_size: this.pageSize } })
        .then(resp => {
          this.totalInvoiceCount = resp.data?.count || 0
          this.mapInvoiceList(resp.data?.results || [])
        })
        .catch(err => { this.handleApiError(err) })
        .finally(() => { this.isLoadingData = false })
    },

    handleApiError(err) {
      let message = this.$t('error_message.unable_to_complete_request')
      if (err.response?.data?.detail) {
        message = err.response.data.detail
      } else if (err.response?.data?.length > 0) {
        message = err.response.data[0]
      } else if (err.response?.data) {
        let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
        if (data) { message = data }
      }
      this.showMessage(['error', message])
    },

    getDisplayedPaymentMethods(methods) {
      if (!methods || methods.length === 0) return []
      return methods.slice(0, 2)
    },

    mapAddress(addr) {
      if (!addr) return
      this.address = {
        line1: addr.address_1 || '',
        line2: addr.address_2 || '',
        postcode: addr.postcode || '',
        city: addr.city || '',
        state: addr.state || '',
        country: addr.country || 'Malaysia'
      }
    },

    saveEinvoice(payload) {
      return this.einvoiceId
        ? this.$axios.patch(`/einvoices/${this.einvoiceId}/`, payload)
        : this.$axios.post('/einvoices/', payload)
    },

    onCloseNotePrompt() {
      this.showNotePrompt = false
    },

    onCreateNote() {
      this.showNotePrompt = false
      this.$emit('create-note', { type: this.notePromptType, einvoiceId: this.einvoiceId })
    },

    onBack() {
      if (this.isViewMode) {
        this.$emit('close')
      } else {
        this.$emit('back')
      }
    },

    onClose() {
      this.$emit('close')
    },

    onViewEInvoice() {
      if (this.shareUrl) {
        window.open(this.shareUrl, '_blank')
      }
    },

    onDownloadPdf() {
      if (!this.einvoiceId) return
      this.isDownloading = true
      this.$axios.get(`/einvoices/${this.einvoiceId}/download/`, { responseType: 'blob' })
        .then(resp => {
          let filename = `einvoice_${this.einvoiceId}.pdf`
          const contentDisposition = resp.headers['content-disposition']
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?(.+)"?/)
            if (match && match[1]) filename = match[1].replace(/"/g, '')
          }
          const blob = new Blob([resp.data])
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = filename
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDownloading = false
        })
    },

    getDateRange() {
      if (!this.dateFilter) return {}
      // Single date: { key: 'date', value: '2025-12-10' }
      if (this.dateFilter.key === 'date') {
        return { start_date: this.dateFilter.value, end_date: this.dateFilter.value }
      }
      // Range: [{ key: 'date__gte', value: '...' }, { key: 'date__lte', value: '...' }]
      if (Array.isArray(this.dateFilter)) {
        const gte = this.dateFilter.find(f => f.key === 'date__gte')
        const lte = this.dateFilter.find(f => f.key === 'date__lte')
        if (gte && lte) return { start_date: gte.value, end_date: lte.value }
      }
      return {}
    },

    buildSavePayload() {
      const { start_date, end_date } = this.getDateRange()
      const payload = {
        company: this.company.id,
        type: 'consolidated',
        start_date,
        end_date,
        submitted_date: this.submittedDate
      }

      if (this.activePaymentMethod === 'self-pay') {
        payload.date_description = this.description
      } else if (this.activePaymentMethod === 'panel') {
        const panelName = this.isViewMode ? this.localPanelName : (this.panelData?.name || '')
        payload.panel = panelName
        payload.customer_name = this.companyName
        payload.customer_tin = this.tinNumber
        payload.customer_phone = this.phoneNumber
        payload.customer_email = this.email
        payload.customer_address = {
          address_1: this.address.line1,
          address_2: this.address.line2,
          postcode: this.address.postcode,
          city: this.address.city,
          state: this.address.state,
          country: this.address.country || 'Malaysia'
        }
        payload.send_email = this.sendCopyToPanel
      }
      return payload
    },

    onSaveDraft() {
      this.isSavingDraft = true
      const payload = this.buildSavePayload()

      this.saveEinvoice(payload)
        .then(() => {
          this.$emit('save')
        })
        .catch(err => { this.handleApiError(err) })
        .finally(() => { this.isSavingDraft = false })
    },

    onSubmit() {
      if (this.$refs.einvoiceForm && !this.$refs.einvoiceForm.validate()) {
        this.showMessage(['error', 'Please fill in all required fields'])
        return
      }

      this.isSubmitting = true
      const payload = this.buildSavePayload()

      this.saveEinvoice(payload)
        .then(resp => {
          const id = this.einvoiceId || resp.data?.id
          // Step 2: Submit
          const submitPayload = this.sendCopyToPanel ? { customer: { email: this.email } } : {}
          return this.$axios.post(`/einvoices/${id}/submit/`, submitPayload)
        })
        .then(() => {
          this.$emit('submit')
        })
        .catch(err => { this.handleApiError(err) })
        .finally(() => { this.isSubmitting = false })
    },

    onOpenInvoice(item) {
      window.open(`/admin/clinic/checkout/${item.pid}`, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .yz-card-text {
  height: 100%;
}
</style>
